import ApiErrorBanner from '@/components/ApiErrorBanner';
import DatePickerNative from '@/components/UI/DatePickerNative';
import { errorToast, successToast } from '@/helpers';
import { _notNil } from '@/littledash';
import type { AnimalApiId, DiseaseInductedAtUpdateRequestV1 } from '@/model/Animal.model';
import type { StudyApiId } from '@/model/Study.model';
import type { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { featuresSelector } from '@/support/Selectors';
import { DateUtils } from '@/utils/Date.utils';
import { ModalActions, ModalContainer, ModalHeader, useModalAction } from '@/utils/modal';

const reasonForChangeSelector = createSelector([featuresSelector], (features) => ({
  reasonForChangeEnabled: features?.reason_for_change === true,
}));

interface UpdateDiseaseInductionDateProps {
  studyApiId: StudyApiId;
  animalApiIds: Array<AnimalApiId>;
  handleCallback: () => void;
}

type FormData = Pick<DiseaseInductedAtUpdateRequestV1, 'disease_inducted_at'>;

export const UpdateDiseaseInductionDate: FC<UpdateDiseaseInductionDateProps> = ({
  studyApiId,
  animalApiIds,
  handleCallback,
}) => {
  const { openModal, closeModal } = useModalAction();
  const { reasonForChangeEnabled } = useSelector(reasonForChangeSelector);
  const {
    response: updateResponse,
    invoke: updateDiseaseInductionDate,
    loading: updating,
  } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyApiId}/animals/disease-inducted-at',
    path: { studyApiId },
    body: { disease_inducted_at: null, animal_api_ids: animalApiIds },
    options: {
      invokeOnInit: false,
      onError: { throw: true, toast: false, capture: true, slug: 'disease-inducted-at-update' },
    },
  });

  const { handleSubmit, control } = useForm<FormData>({
    defaultValues: {
      disease_inducted_at: DateUtils.dateNow(),
    },
  });

  const saveDiseaseInductionDate = async (
    diseaseInductedAt: FormData['disease_inducted_at'],
    reasonForChange?: string
  ) => {
    try {
      await updateDiseaseInductionDate({
        path: { studyApiId },
        body: {
          disease_inducted_at: diseaseInductedAt,
          animal_api_ids: animalApiIds,
          ...(_notNil(reasonForChange) ? { reason_for_change: reasonForChange } : {}),
        },
      });
      successToast(`Updated ${animalApiIds.length} disease induction date${animalApiIds.length !== 1 ? 's' : ''}`);
      handleCallback();
    } catch {
      errorToast('Failed to update disease induction date');
    }
  };

  const onSubmit = async ({ disease_inducted_at }: FormData) => {
    if (reasonForChangeEnabled) {
      openModal('BULK_REASON_FOR_CHANGE', {
        title: 'Animal information has changed',
        onClick: (reasonForChange?: string) => saveDiseaseInductionDate(disease_inducted_at, reasonForChange),
      });
      return;
    }
    await saveDiseaseInductionDate(disease_inducted_at);
  };

  return (
    <ModalContainer size="narrow" className="bg-white">
      <div className="flex flex-column bb b--moon-gray">
        <ModalHeader title="Set disease induction date" className="pl3 pt3 pb2 w-100" />
        <div className="f6 lh-copy pl3 pb2" />
      </div>
      {updateResponse?.type === 'error' && <ApiErrorBanner error={updateResponse.error} />}
      <div className="pl3" style={{ width: 290, height: 100 }}>
        <Controller
          name="disease_inducted_at"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <div className="pt3">
              <label htmlFor="disease_inducted_at">Disease induction date</label>
              <DatePickerNative
                id="disease_inducted_at"
                name="disease_inducted_at"
                value={value ?? ''}
                onChange={onChange}
                onBlur={onBlur}
              />
            </div>
          )}
        />
      </div>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        submitBtnText="Save"
        cancelBtnText="Cancel"
        submitButtonProps={{ disabled: updating, loading: updating }}
        cancelButtonProps={{ disabled: updating }}
      />
    </ModalContainer>
  );
};
